import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Merge, ArrowLeftRight, ShieldCheck, CalendarDays, Loader2, AlertTriangle } from "lucide-react";
import { API } from "../App";

const COUNT_LABELS = { session_reports: "séances", exercise_assignments: "exercices assignés", exercise_responses: "réponses d'exercices", psychoeducation_assignments: "modules", appointments: "RDV", mood_entries: "humeurs", anamneses: "dossier" };
const counts = (c) => Object.entries(c || {}).map(([k, v]) => `${v} ${COUNT_LABELS[k] || k}`).join(" · ") || "aucune donnée";

const AccountCard = ({ p, role, testId }) => (
  <div className={`rounded-lg border p-3 text-sm space-y-1 ${role === "keep" ? "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-amber-300 bg-amber-50/50 dark:bg-amber-950/20"}`} data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">{role === "keep" ? "Compte conservé" : "Doublon fusionné (désactivé)"}</p>
    <p className="font-medium">{p.name}</p>
    <p className="text-xs text-muted-foreground break-all">{p.email}</p>
    <div className="flex flex-wrap gap-1 text-[10px]">
      {p.has_account ? <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> accès Kintsugi</span> : <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">sans compte</span>}
      {p.source === "calendly" && <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Calendly</span>}
    </div>
    <p className="text-xs text-muted-foreground">{counts(p.counts)}</p>
  </div>
);

export const MergePatientDialog = ({ open, onOpenChange, patientId, onMerged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dupId, setDupId] = useState("");
  const [swap, setSwap] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setDupId(""); setSwap(false); setConfirm(false);
    axios.get(`${API}/therapist/patients/${patientId}/merge-candidates`, { withCredentials: true })
      .then((r) => { setData(r.data); const best = r.data.candidates[0]; if (best && best.similarity >= 0.7) setDupId(best.user_id); })
      .catch(() => toast.error("Chargement impossible"));
  }, [open, patientId]);

  const other = data?.candidates.find((c) => c.user_id === dupId);
  const keep = swap ? other : data?.patient;
  const dup = swap ? data?.patient : other;
  useEffect(() => { if (data && other) setSwap(!data.patient.has_account && other.has_account); }, [dupId]);

  const merge = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/therapist/patients/${keep.user_id}/merge`, { duplicate_id: dup.user_id }, { withCredentials: true });
      const moved = Object.values(r.data.moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Fusion effectuée : ${moved} élément(s) rattaché(s) à ${r.data.kept.name}. L'adresse ${dup.email} est désormais un alias.`);
      onOpenChange(false); onMerged?.(r.data);
      if (keep.user_id !== patientId) navigate(`/therapist/patients/${keep.user_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fusion impossible"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="merge-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5 text-primary" /> Fusionner un compte en doublon</DialogTitle>
          <DialogDescription>Quand un patient a pris rendez-vous (Calendly) avec une adresse différente de celle de son compte Kintsugi, ses séances et ses exercices sont séparés. La fusion rattache toutes les données au compte conservé et transforme l'autre adresse en alias pour les prochains rendez-vous.</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground py-6 text-center">Chargement…</p> : (
          <div className="space-y-4">
            <label className="block text-sm font-medium">Doublon à fusionner avec <span className="text-primary">{data.patient.name}</span>
              <select value={dupId} onChange={(e) => setDupId(e.target.value)} className="mt-1 w-full h-9 px-2 text-sm border rounded-lg bg-background" data-testid="merge-duplicate-select">
                <option value="">— Choisir un patient —</option>
                {data.candidates.map((c) => <option key={c.user_id} value={c.user_id}>{c.name} · {c.email}{c.similarity >= 0.7 ? " · ressemblance forte" : ""}{c.has_account ? " · accès" : " · sans compte"}</option>)}
              </select>
            </label>
            {keep && dup && (
              <>
                <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <AccountCard p={keep} role="keep" testId="merge-keep-card" />
                  <Button variant="ghost" size="icon" onClick={() => setSwap((v) => !v)} title="Inverser : conserver l'autre compte" data-testid="merge-swap-btn"><ArrowLeftRight className="w-4 h-4" /></Button>
                  <AccountCard p={dup} role="dup" testId="merge-dup-card" />
                </div>
                {!keep.has_account && dup.has_account && <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="merge-access-warning"><AlertTriangle className="w-3.5 h-3.5" /> Le doublon possède l'accès Kintsugi et le compte conservé non : inversez pour garder l'accès du patient.</p>}
                <label className="flex items-start gap-2 text-xs"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5" data-testid="merge-confirm-check" /> Je confirme la fusion : les données du doublon seront rattachées à « {keep.name} » ({keep.email}) et le doublon sera désactivé. Cette opération n'est pas réversible.</label>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button onClick={merge} disabled={!keep || !dup || !confirm || busy} className="gap-1.5" data-testid="merge-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Merge className="w-4 h-4" />} Fusionner</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
